import { ApiInstance } from "@/core/apiInstance";
import { jsonToFormData, logEvent, mapToLegacyForm, transformPacketTypeToId } from '@/core/helpers';
import { DeductionOptions, ExtraFilters, VehicleTypes } from '@/enums/filters';
import router from "@/router";
import { format, parse } from 'date-fns';
import { createErrorObject } from './errorHandler';

const user = ApiInstance();
const apiInstance = user.apiInstance()

const makeAPIRequest = async (method, url, data, config = {}) => {
  try {
    if (user.isPanelIFrame()){
      config.params = {
        '_netusr': router.currentRoute.value.query['_netusr'],
        '_offerusr': router.currentRoute.value.query['_offerusr'],
        'offer_code': router.currentRoute.value.query['offer_code'],
        '_issue': router.currentRoute.value.query['_issue'],
        'autocomplete': router.currentRoute.value.query['autocomplete'],
        'loggedinuser': router.currentRoute.value.query['loggedinuser'],
        'section': router.currentRoute.value.query['section']
      }
    }
    const response = await apiInstance({ method, url, data, ...config });

    return response.data;
  } catch (error) {
    throw createErrorObject(error);
  }
};

const fetchInitialDataFromLegacy = async (fid) => {
  if (!fid) {
    return;
  }

  const url = `/userTempData/getUserData/${fid}`;
  return makeAPIRequest('get', url);
};

const fetchUploadDataFromLegacy = async (offerCode) => {
  if (!offerCode) {
    return;
  }

  const url =
    `/document/get_needed_documents?offer_code=${encodeURIComponent(offerCode)}`;

  return makeAPIRequest('get', url);
};

const fetchPaymentDataFromLegacy = async (id, paymentCode, source = true) => {
  if (!id || !paymentCode) {
    return;
  }

  const baseUrl =
    `/OfferPayment/payment_data/id/${encodeURIComponent(id)}` +
    `/payment_code/${encodeURIComponent(paymentCode)}`;

  const url = source
    ? `${baseUrl}?utm_source=email&utm_medium=chu`
    : baseUrl;

  return makeAPIRequest('get', url);
};

const fetchTranckingDataFromLegacy = async (fid) => {
  if (!fid) {
    return;
  }

  const url = `/userTempData/getTrackingData/${fid}`;
  return makeAPIRequest('get', url);
};

const fetchFormDataFromLegacy = async (fid) => {
  if (!fid) {
    return;
  }

  const url = `/userTempData/getFormData/${fid}`;
  return makeAPIRequest('get', url);
};

const sendCompareDataToLegacy = async data => {
  const url = `/userTempData/postCompareData`;

  return makeAPIRequest('post', url, data)
}

const getBuyNowRedirectLink = async (payload) => {
  const url = '/userTempData/initListingData';

  try {
    return await makeAPIRequest('post', url, payload);
  } catch (error) {
    await logEvent("Failed initListingData call", "error", {error: error, payload: payload});

    throw error;
  }
}

const postDataForRepricingToLegacy = async (fid, data, customerJourneyId) => {
  if (!fid) {
    return;
  }

  let parsedDate = parse(data.startingDate, 'EEE MMM dd yyyy', new Date());

  let transformedData = {
    "startDate": format(parsedDate, 'dd/MM/yyyy'),
    "vehicleValue": data.vehicleValue.toString(),
    "packetType": data.insuranceType,
    "duration": data.insuranceDuration.toString(),
    "selectedFeatures": data.insuranceCovers,
    "fid": fid,
    "afm": data.afm,
    "email": data.email
  }

  // Enable EIQ validation
  if (data.vehicleType === VehicleTypes.Car || data.vehicleType === VehicleTypes.Bike) {
      transformedData = {
          vehicle: {
              value: data.vehicleValue
          },
          contract: {
              duration: data.insuranceDuration,
              packetType: transformPacketTypeToId(data.insuranceType),
              startDate: format(parsedDate, 'yyyy-MM-dd') + "T01:01:01+01:00",
          },
          context: {
              classification: {
                  type: data.vehicleType
              }
          },
          offerDetails: {
              customerSelection: {
                  filtersFeaturesTypes: data.insuranceCovers,
              },
          },
          trackingData: {
              customerJourneyId: customerJourneyId,
          },
          fid: fid
      }

      if (data.email || data.afm) {
          transformedData.customer ??= {};
          transformedData.customer.drivers = [
              {
                  email: data.email,
                  taxNo: data.afm
              }
          ]
      }
  }

  const url = `/userTempData/updateUserData`;
  return makeAPIRequest('post', url, transformedData);
};

const postDeductibleDataToLegacy = async(data) => {
  const url = `/userTempData/updateUserData`;
  return makeAPIRequest('post', url, data);
}

const getOffersByEmailToLegacy = async (fid, email) => {
  if (!fid) {
    return;
  }

  const data = `opt_in_email[email]=${encodeURIComponent(email)}&_f=${encodeURIComponent(fid)}`;
  const url = `/opt-in-best-price`;

  return makeAPIRequest('post', url, data, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
  });
};

const postDataForDatabaseToLegacy = async (data) => {
  const transformedData = {}

  transformedData.stats_quote = [...data.packagesStore.packagesWithErrors, ...data.packagesStore.filteredAndSortedPackages, ...data.packagesStore.filteredOutPackages].map((e, i) => {
    if (e.errorMessage) {
      return ({
        'stats_quote': {
          'car_pb_query_id': data.quoteStore.quote?.trackingData?.preBuyID,
          'packet_id': (e.payload.multiproduct.products ?? []).find(el => el.type.name == "group_covers")?.attributes?.siteId,
          'price': e.price ?? 0,
          'position': 0,
          'shown': 0,
          'company_id': (e.payload.multiproduct.products ?? []).find(el => el.type.name == "group_covers")?.vendor?.attributes?.siteId
        },
        'stats_quote_error': {
          'error_id': null,
          'error_group_id': null,
          'error_message': e.errorMessage
        }
      })
    } else {
      return ({
        'stats_quote': {
          'car_pb_query_id': data.quoteStore.quote?.trackingData?.preBuyID,
          'packet_id': e.packageId,
          'price': e.price,
          'position': data.packagesStore.filteredAndSortedPackages.indexOf(data.packagesStore.filteredAndSortedPackages.find((el, i) => el.id === e.id)) + 1,
          'shown': 1,
          'company_id': e.company.id
        }
      })
    }
  })

  transformedData.stats_quote_options = {
    '_f': data.quoteStore.fid,
    'pb_query_id': data.quoteStore.quote?.trackingData?.preBuyID,
        'covers': data.quoteStore.appliedFilters?.insuranceCovers,
        'category': data.quoteStore.quote?.category,
        'frequency': 12 / data.quoteStore.appliedFilters?.insuranceDuration,
        'packet_type': data.quoteStore.quote?.context?.classification?.filters?.packetType,
        'value': data.quoteStore.appliedFilters?.vehicleValue,
        'start_date': new Date(new Date(data.quoteStore.appliedFilters?.startingDate) - new Date(data.quoteStore.appliedFilters?.startingDate).getTimezoneOffset() * 60000).toISOString().split('T')[0]
  }

  transformedData._f = data.quoteStore.fid

  const url = `/userTempData/updateDatabase`;
  await makeAPIRequest('post', url, transformedData);

  await postInstantFiltersToLegacy(data.quoteStore.fid, data.quoteStore.appliedFilters, data.packagesStore.sorting)
};

const postUpsellUserSelection = async (fid, data) => {
  const url = `/userTempData/saveUpsellUserSelection`;
  const payload = {
    '_f': fid,
    'covers': data.covers,
  }

  return makeAPIRequest('post', url, payload);
}

const postPaymentMethodSelection = async (pbId, paymentMethod) => {
  if (!pbId || !paymentMethod) {
    return;
  }

  const url = `/userTempData/savePaymentMethodSelection`;
  const payload = {
    'pb_id': pbId,
    'payment_method': paymentMethod,
  }

  return makeAPIRequest('post', url, payload);
}

const postInstantFiltersToLegacy = async (f, data, sorting) => {
  let extraFilters = data.extraFilters
  .filter(e => 
    e === ExtraFilters.EasyBuy || 
    e === ExtraFilters.Offers || 
    e === ExtraFilters.ShortDrive
  )
  .map(e => {
    switch(e){
      case ExtraFilters.EasyBuy: return 1
      case ExtraFilters.Offers: return 2
      case ExtraFilters.ShortDrive: return 3
    }
  })

  let naturalPhenomena = data.extraFilters
  .filter(e => 
    e === ExtraFilters.BasicNaturalPhenomena || 
    e === ExtraFilters.ExtraNaturalPhenomena || 
    e === ExtraFilters.PremiumNaturalPhenomena
  )
  .map(e => {
    switch(e){
      case ExtraFilters.BasicNaturalPhenomena: return 1
      case ExtraFilters.ExtraNaturalPhenomena: return 2
      case ExtraFilters.PremiumNaturalPhenomena: return 3
    }
  })

  let deductionOptions = data.deductionOptions.map(e => {
    switch(e){
      case DeductionOptions.d0to250.value: return 1
      case DeductionOptions.d0to500.value: return 2
      case DeductionOptions.d251toMax.value: return 3
      case DeductionOptions.d251to600.value: return 4
      case DeductionOptions.d501to900.value: return 5
      case DeductionOptions.d901toMax.value: return 6
      case DeductionOptions.d601to1000.value: return 7
      case DeductionOptions.d1001toMax.value: return 8
      case DeductionOptions.withoutDeduction.value: return 9
      case DeductionOptions.withDeduction.value: return 10
    }
  })

  let sortOption = 0
  switch(sorting){
    case 'price': sortOption = 0; break;
    case 'benefits': sortOption = 1; break;
    case 'rating': sortOption = 2; break;
  }

  let payload = {
    '_f': f,
    'extraFilters': extraFilters.length === 0 ? null : extraFilters.join(','),
    'deductionOptions': deductionOptions.length === 0 ? null : deductionOptions.join(','),
    'naturalPhenomena': naturalPhenomena.length === 0 ? null : naturalPhenomena.join(','),
    'sortOption': sortOption
  }

  const url = `/userTempData/updateInstantFilters`;
  return makeAPIRequest('post', url, payload);
}

const postDataForValidation = async (data, fid, validationPath) =>{
  const url = `${validationPath}/form_validate/_f/${fid}`;
  return makeAPIRequest('post', url, data);
}

const postCheckoutDataForValidation = async (category, dc, fid, data, optins, copmanySiteId, urlExt) =>{
  let url = `/${category}insurance/checkout_validate/ct/formorder/_f/${fid}/_dc/${dc}`;

  if (urlExt) {
    url += `/${urlExt}`;
  }
  
  let values = {
    ...data,
    company_site_id: copmanySiteId
  }
  
  const payload = {
    values,
    optins
  }

  return makeAPIRequest('post', url, mapToLegacyForm(payload));
}

const applyAnytimeDiscount = async (code, fid, dc) => {
  const url = `/cosmoteDFY/submit_code`;
  const payload = {
    "_auth": "dLqN6JXGR1BANEC7Mjw4Za_SLASH_j3E3g22pUB4RiStXkj1kHeKmNtlISQ52Mx6XGLlnd9bcdfftcORC16bQBSSJFjw==",
		"coupon_code": code,
		"_f": fid,
		"_dc": dc
  }

  const formDataPayload = jsonToFormData(payload);

  return makeAPIRequest('post', url, formDataPayload);
}

const DiscountTypes = Object.freeze({
  ANYTIME: 'cosmoteDFY',
  EUROLIFE: 'vodafone',
});

const clearDiscount = async (fid, dc, discountType) => {
  const url = `/${discountType}/clear_code`;
  const payload = {
    "_auth": "dLqN6JXGR1BANEC7Mjw4Za_SLASH_j3E3g22pUB4RiStXkj1kHeKmNtlISQ52Mx6XGLlnd9bcdfftcORC16bQBSSJFjw==",
		"_f": fid,
		"_dc": dc
  }

  const formDataPayload = jsonToFormData(payload);

  return makeAPIRequest('post', url, formDataPayload);
}

const applyEurolifeDiscount = async (code, fid, dc) => {
  const url = `/vodafone/submit_code`;
  const payload = {
    _auth:
      'dLqN6JXGR1BANEC7Mjw4Za_SLASH_j3E3g22pUB4RiStXkj1kHeKmNtlISQ52Mx6XGLlnd9bcdfftcORC16bQBSSJFjw==',
    coupon_code: code,
    _f: fid,
    _dc: dc,
  };

  const formDataPayload = jsonToFormData(payload);

  return makeAPIRequest('post', url, formDataPayload);
}

const getOrderStatus = async (fid, dc, category, ct = 'ct') => {
  if (!fid || !dc) {
    throw new Error('Missing fid or dc for completeCheckoutAjax');
  }

  const url = `/${category}insurance/complete/${ct}/formorder/_f/${encodeURIComponent(fid)}/_dc/${encodeURIComponent(dc)}`;
  return makeAPIRequest('get', url);
}

const uploadFiles = async (payload) => {
  const url = `/document/upload`;

  return makeAPIRequest('post', url, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Accept': 'application/json, text/javascript, */*; q=0.01',
    },
  });
}

const postNoPreviousInsurancePolicy = async (offerCode, hasPreviousPolicy) => {
  const url = `/document/no_previous_insurance_policy`;
  return makeAPIRequest('post', url, jsonToFormData({ offer_code: offerCode, has_previous_policy: hasPreviousPolicy }));
};

const checkEmail = async (email) => {
  return makeAPIRequest('post', '/contest/check_email', { email });
};

const saveContestEntry = async (email, preBuyID, acceptedTerms, vehicleCategory, acceptedNewsletter = false) => {
  return makeAPIRequest('post', '/contest/save_entry', {
    email,
    pb_id: preBuyID,
    category: vehicleCategory,
    accepted_terms: acceptedTerms,
    accepted_newsletter: acceptedNewsletter,
  });
};


export {
  applyAnytimeDiscount,
  applyEurolifeDiscount, checkEmail, clearDiscount,
  DiscountTypes, fetchFormDataFromLegacy,
  fetchInitialDataFromLegacy, fetchPaymentDataFromLegacy,
  fetchTranckingDataFromLegacy, fetchUploadDataFromLegacy, getBuyNowRedirectLink,
  getOffersByEmailToLegacy, getOrderStatus, postCheckoutDataForValidation,
  postDataForDatabaseToLegacy,
  postDataForRepricingToLegacy,
  postDataForValidation, postDeductibleDataToLegacy,
  postInstantFiltersToLegacy, postNoPreviousInsurancePolicy, postPaymentMethodSelection, postUpsellUserSelection, saveContestEntry, sendCompareDataToLegacy, uploadFiles
};

